/* eslint-disable spire/export-styles */
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import { ColumnAlignment } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import baseTheme, { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem, { GridWidths } from "@insite/mobius/GridItem";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import gridVerticalGapField, {
    getVerticalGapData,
    VerticalGap,
} from "@tCentro/Components/Common/Block/fields/gridVerticalGap";
import { gridGapStyles } from "@tCentro/theme/mixins";
import * as React from "react";
import { css } from "styled-components";

const enum fields {
    columns = "columns",
    gap = "gap",
    alignment = "alignment",
    reflow = "reflow",
    gridVerticalGap = "gridVerticalGap",
    mobileFullWidth = "mobileFullWidth",
    cssClass = "cssClass",
}

interface Props extends WidgetProps {
    fields: {
        [fields.columns]: GridWidths[];
        [fields.gap]: number;
        [fields.alignment]: ColumnAlignment[];
        [fields.reflow]: boolean;
        [fields.gridVerticalGap]: VerticalGap;
        [fields.mobileFullWidth]: boolean;
        [fields.cssClass]: string;
    };
}

const mobileColumnsRef: { [key: number]: GridWidths | GridWidths[] } = {
    1: [3, 3, 1, 1, 1],
    2: [4, 4, 2, 2, 2],
    3: [6, 6, 3, 3, 3],
    4: [6, 6, 4, 4, 4],
    5: [12, 12, 5, 5, 5],
    6: [12, 12, 6, 6, 6],
    7: [12, 12, 7, 7, 7],
    8: [12, 12, 8, 8, 8],
    9: [12, 12, 9, 9, 9],
    10: [12, 12, 10, 10, 10],
    11: [12, 12, 11, 11, 11],
    12: 12,
};

const convertToResponsive = (column: GridWidths): GridWidths | GridWidths[] => {
    return mobileColumnsRef[column] || column;
};

export interface FooterRowStyles {
    container?: InjectableCss;
    containerMobileFullwidth?: InjectableCss;
}

export const footerRowStyles: FooterRowStyles = {
    container: {
        css: css`
            > div {
                margin-right: auto;
                margin-left: auto;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            padding-right: 20px;
                            padding-left: 20px;
                        `,
                        css`
                            padding-right: 30px;
                            padding-left: 30px;
                        `,
                        css`
                            padding-right: 45px;
                            padding-left: 45px;
                        `,
                        css`
                            padding-right: 45px;
                            padding-left: 45px;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    containerMobileFullwidth: {
        css: css`
            > div {
                margin-right: auto;
                margin-left: auto;
            }
            &.paddingLeftRight16MobileTab {
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            padding-right: 16px !important;
                            padding-left: 16px !important;
                        `,
                        css`
                            padding-right: 16px !important;
                            padding-left: 16px !important;
                        `,
                        css`
                            padding-right: 16px !important;
                            padding-left: 16px !important;
                        `,
                        css`
                            padding-right: 45px;
                            padding-left: 45px;
                        `,
                        null,
                    ])}
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            padding-right: 0;
                            padding-left: 0;
                        `,
                        css`
                            padding-right: 0;
                            padding-left: 0;
                        `,
                        css`
                            padding-right: 0;
                            padding-left: 0;
                        `,
                        css`
                            padding-right: 45px;
                            padding-left: 45px;
                        `,
                    ],
                    "min",
                )}

            &.paddingLeftRightPunchoutRow {
                padding-left: 64px;
                padding-right: 64px;
                max-width: 1800px;
                margin: auto;
                margin-top: 40px;
                margin-bottom: 64px;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            padding-right: 16px !important;
                            padding-left: 16px !important;
                        `,
                        css`
                            padding-right: 16px !important;
                            padding-left: 16px !important;
                        `,
                        css`
                            padding-right: 16px !important;
                            padding-left: 16px !important;
                        `,
                        css``,
                        null,
                    ])}

                > div[class^="GridWrapper"] {
                    > div[class^="GridOffset"] {
                        > div[class^="GridItemStyle"]:first-child {
                            position: relative;
                            &::after {
                                content: "";
                                position: relative;
                                top: 0px;
                                right: -68px;
                                border: 2px solid #f2f2f2;
                                width: 2px;
                                height: 100%;
                                ${({ theme }: { theme: BaseTheme }) =>
                                    breakpointMediaQueries(theme, [
                                        css`
                                            display: none;
                                        `,
                                        css`
                                            display: none;
                                        `,
                                        css`
                                            display: none;
                                        `,
                                        css`
                                            @media (min-width: 993px) and (max-width: 1400px) {
                                                right: -40px !important;
                                            }
                                        `,
                                        null,
                                    ])}
                            }
                        }
                    }
                }
            }
        `,
    },
};

const styles = footerRowStyles;

const Row: React.FunctionComponent<Props> = (props: Props) => {
    const {
        fields: { columns, gap, alignment, reflow, mobileFullWidth },
        id,
    } = props;

    const columnElements: JSX.Element[] = [];

    for (let x = 0; x < columns.length; x += 1) {
        const align = alignment[x] ? alignment[x] : "top";
        columnElements.push(
            <GridItem key={x} width={reflow ? convertToResponsive(columns[x]) : columns[x]} align={align}>
                <Zone contentId={id} zoneName={`Content${x}`} />
            </GridItem>,
        );
    }

    /* Custom Code */
    const {
        fields: { gridVerticalGap, cssClass },
    } = props;

    const gridContainerProps = {
        gap: gridVerticalGap ? 0 : gap || 0,
        css: css`
            ${gridVerticalGap ? gridGapStyles([gap ? `${gap}px` : 0], getVerticalGapData(gridVerticalGap)) : null}
        `,
    };

    return (
        <>
            {mobileFullWidth ? (
                <StyledWrapper {...styles.containerMobileFullwidth} className={cssClass}>
                    <GridContainer {...gridContainerProps}>{columnElements}</GridContainer>
                </StyledWrapper>
            ) : (
                <StyledWrapper {...styles.container} className={cssClass}>
                    <GridContainer {...gridContainerProps}>{columnElements}</GridContainer>
                </StyledWrapper>
            )}
        </>
    );
};
const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const widgetModule: WidgetModule = {
    component: Row,
    definition: {
        group: "Basic",
        icon: "distribute-spacing-vertical",
        fieldDefinitions: [
            {
                name: fields.columns,
                displayName: "Layout",
                editorTemplate: "ColumnsField",
                defaultValue: [4, 4, 4],
                fieldType: "General",
                sortOrder: 1,
                validate: value => (value.length > 0 ? null : "Please enter a valid layout."),
            },
            {
                name: fields.reflow,
                displayName: "Use default reflow for responsive screens sizes",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
            {
                name: fields.gap,
                displayName: "Gap Between Items",
                editorTemplate: "IntegerField",
                defaultValue: 0,
                fieldType: "General",
                max: 1000,
                min: 0,
                sortOrder: 10,
            },
            {
                name: fields.alignment,
                displayName: "Column Alignment",
                editorTemplate: "ColumnAlignmentField",
                defaultValue: [],
                fieldType: "General",
                sortOrder: 1,
                tab: advancedTab,
            },
            {
                name: fields.mobileFullWidth,
                displayName: "Mobile full width",
                editorTemplate: "CheckboxField",
                fieldType: "Translatable",
                defaultValue: false,
            },
            {
                name: fields.cssClass,
                displayName: "CSS class name",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "",
            },
        ],
    },
};

export default widgetModule;
